import Typography from '@mui/material/Typography';
import { Luggage, NoLuggage } from '@mui/icons-material';
import { Stack } from '@mui/system';
import { UnitOption, PassengerType, BaggageItem } from '~/types';
import { getFriendlyPassengerType } from '~/modules/helpers';
import { Divider, useTheme, useMediaQuery } from '@mui/material';

interface BaggageDisplayProps {
  bags: Record<string, BaggageItem>;
  iconSize?: number;
  textSize?: number;
  isSearchPage?: boolean;
}

const formatBaggageDisplay = (value: string, unit: string, isSearchPage = false, isMobile = false) => {
  const displayValue = unit === UnitOption.QUANTITY ? `x ${value}` : value;
  const displayValueOnly = unit === UnitOption.QUANTITY ? `${value} pieces` : value;
  const displayUnit = unit !== UnitOption.QUANTITY ? unit.toLowerCase() : '';

  if (isMobile) {
    return !isSearchPage
      ? `${displayValue}${displayUnit}`
      : `Up to ${displayValueOnly}${displayUnit}`;
  }

  return !isSearchPage
    ? `${displayValue}${displayUnit} Check-in baggage included`
    : `Up to ${displayValueOnly}${displayUnit} Check-in baggage included/passenger`;
};

const BookingExtras = ({ bags, iconSize = 16, textSize = 12, isSearchPage = false }: BaggageDisplayProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [firstPassengerType] = Object.values(bags);
  const bagsSameForAllPassengers = Object.values(bags).every(
    ({ unit, value }) => unit === firstPassengerType.unit && value === firstPassengerType.value
  );

  return (
    <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={2}>
      {
        bagsSameForAllPassengers || isSearchPage ? (
          parseInt(firstPassengerType.value) ? (
            <Typography fontSize={textSize} display="inline-flex" alignItems="center">
              <Luggage sx={{ fontSize: iconSize }} style={{ marginRight: 2 }} />
              {formatBaggageDisplay(firstPassengerType.value, firstPassengerType.unit, isSearchPage, isMobile)}
            </Typography>
          ) : (
            <Typography fontSize={textSize} display="inline-flex" alignItems="center">
              <NoLuggage sx={{ fontSize: iconSize }} style={{ marginRight: 2 }} />
              No check-in baggage included
            </Typography>
          )
        ) : (
          isMobile
            ? (
              Object.values(
                Object.entries(bags).reduce((acc, [ptype, { value, unit }]) => {
                  const key = `${value}-${unit}`;
                  acc[key] = acc[key] || { types: [], value, unit };
                  acc[key].types.push(ptype);
                  return acc;
                }, {} as Record<string, { types: string[]; value: string; unit: string }>)
              ).map((group, index) => (
                parseInt(group.value) ? (
                  <Typography key={index} fontSize={textSize} display="inline-flex" alignItems="center">
                    <Luggage sx={{ fontSize: iconSize }} style={{ marginRight: 2 }} />
                    ({group.types.map(pt => getFriendlyPassengerType(pt as PassengerType)).join('/')}){' '}
                    {formatBaggageDisplay(group.value, group.unit, false, isMobile)}
                  </Typography>
                ) : (
                  <Typography key={index} fontSize={textSize} display="inline-flex" alignItems="center">
                    <NoLuggage sx={{ fontSize: iconSize }} style={{ marginRight: 2 }} />
                    ({group.types.map(pt => getFriendlyPassengerType(pt as PassengerType)).join('/')}) No check-in baggage included
                  </Typography>
                )
              ))
            )
            : (
              Object.keys(bags).map((ptype, index) => (
                parseInt(bags[ptype].value) ? (
                  <Typography key={index} fontSize={textSize} display="inline-flex" alignItems="center">
                    <Luggage sx={{ fontSize: iconSize }} style={{ marginRight: 2 }} />
                    ({getFriendlyPassengerType(ptype as PassengerType)}){' '}
                    {formatBaggageDisplay(bags[ptype].value, bags[ptype].unit, false, isMobile)}
                  </Typography>
                ) : (
                  <Typography key={index} fontSize={textSize} display="inline-flex" alignItems="center">
                    <NoLuggage sx={{ fontSize: iconSize }} style={{ marginRight: 2 }} />
                    ({getFriendlyPassengerType(ptype as PassengerType)}) No check-in baggage included
                  </Typography>
                )
              ))
            )
        )
      }
    </Stack>
  );

};

export default BookingExtras;
